// src/utils/apiClient.ts
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { addBreadcrumb, captureException, captureMessage } from '@sentry/react';
import { getCookie } from 'src/utils/cookies';
import { jwtDecode } from 'src/utils/jwtDecode';
import { checkTokenExpiry } from 'src/utils/apiClient';
import { fetchNewToken } from 'src/utils/fetchNewToken';
import { REFRESH_TOKEN, SSO_STATUS, SSO_SESSION_BASE } from 'src/constants/httpEndPoints';

const isSsoPreAuthRequest = (url?: string) =>
  !!url && (url === SSO_STATUS || url.startsWith(SSO_SESSION_BASE));

// Manage refresh token logic
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string | null) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string | null) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Axios request interceptor
axios.interceptors.request.use(
  async (config) => {
    if (config.url === REFRESH_TOKEN || isSsoPreAuthRequest(config.url)) {
      return config;
    }

    let token: string | null | undefined = getCookie('user');

    if (token) {
      const { exp } = jwtDecode(token);
      const isTokenExpired = checkTokenExpiry(exp);

      if (isTokenExpired) {
        if (!isRefreshing) {
          isRefreshing = true;
          token = await fetchNewToken();
          isRefreshing = false;
          onRefreshed(token);
        } else {
          const retryOriginalRequest = new Promise<string | null>((resolve) => {
            subscribeTokenRefresh(resolve);
          });

          token = await retryOriginalRequest;
        }

        if (!token) {
          return Promise.reject(new Error('Token expired and refresh failed'));
        }
      }

      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      } as AxiosRequestConfig['headers'];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Retry logic for safe methods
const SAFE_HTTP_METHODS = ['get', 'head', 'options'];

const retryRequest = async (
  config: AxiosRequestConfig,
  retries: number = 1,
  backoffMs: number = 500,
): Promise<AxiosResponse> => {
  const method = config.method?.toLowerCase();

  if (!method || !SAFE_HTTP_METHODS.includes(method)) {
    throw new Error(`Retry not allowed for HTTP method: ${method}`);
  }

  try {
    return await axios(config);
  } catch (error: any) {
    if (error?.response?.status === 429) {
      const retryAfter = parseInt(error.response.headers['retry-after'], 10) * 1000 || 5000;
      await new Promise((resolve) => setTimeout(resolve, retryAfter));
      return retryRequest(config, retries, backoffMs);
    }

    if (retries > 0 && error?.response?.status >= 500 && error?.response?.status < 600) {
      addBreadcrumb({
        category: 'api.retry',
        message: `Retrying API call to ${config.url}. Retries left: ${retries}`,
        level: 'info',
        data: {
          method: method.toUpperCase(),
          url: config.url,
          status: error.response?.status,
        },
      });

      await new Promise((resolve) => setTimeout(resolve, backoffMs));
      return retryRequest(config, retries - 1, backoffMs * 2);
    }

    throw error;
  }
};

// API functions
const monitorApiCall = async (
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  URL: string,
  payload?: unknown,
  options?: AxiosRequestConfig<unknown>,
  skipRetry?: boolean,
): Promise<AxiosResponse> => {
  const token = getCookie('user');
  const headers = { ...(options?.headers ? { ...options.headers } : {}) };

  const config: AxiosRequestConfig = {
    ...(options || {}),
    url: URL,
    method,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      ...headers,
    },
    data: payload,
  };

  const startTime = Date.now();

  try {
    const methodSafe = SAFE_HTTP_METHODS.includes(method) && !skipRetry;
    const response = methodSafe ? await retryRequest(config) : await axios(config);

    const endTime = Date.now();
    const duration = endTime - startTime;

    if (duration > 5000) {
      captureMessage(`API took more than 5 seconds: ${URL}`, {
        level: 'warning',
        extra: { durationMs: duration, method: method.toUpperCase(), url: URL },
      });
    }

    return response;
  } catch (error: any) {
    const statusCode = error?.response?.status;

    if (statusCode && statusCode >= 500 && statusCode < 600) {
      captureException(error, {
        tags: { url: URL, method: method.toUpperCase() },
        extra: {
          statusCode,
          responseData: error.response?.data,
        },
      });
    }

    throw error;
  }
};

// CRUD wrappers
export const get = async (
  URL: string,
  options?: AxiosRequestConfig<unknown>,
  skipRetry?: boolean,
) => monitorApiCall('get', URL, undefined, options, skipRetry);

export const post = async (URL: string, payload?: unknown, options?: AxiosRequestConfig<unknown>) =>
  monitorApiCall('post', URL, payload, options);

export const patch = async (URL: string, payload: unknown, options?: AxiosRequestConfig<unknown>) =>
  monitorApiCall('patch', URL, payload, options);

export const put = async (URL: string, payload: unknown, options?: AxiosRequestConfig<unknown>) =>
  monitorApiCall('put', URL, payload, options);

export const _delete = async (URL: string, options?: AxiosRequestConfig<unknown>) =>
  monitorApiCall('delete', URL, undefined, options);

// Export all
const apiClient = {
  get,
  post,
  patch,
  put,
  _delete,
};

export { apiClient };
